import { Typography } from "@material-ui/core"
import { createColumnHelper } from "@tanstack/react-table"
import dayjs from "dayjs"
import PropTypes from "prop-types"
import { useContext, useEffect, useState } from "react"
import { useInfiniteQuery } from "react-query"
import { pathAnalytics } from "~/actions/paths"
import AnalyticsTable from "~/components/Analytics/AnalyticsTable"
import BasicSortHeader from "~/components/Analytics/BasicSortHeader"
import PathContext from "~/contexts/Path"
import usePermissionsRedirect from "~/hooks/usePermissionsRedirect"
import Page from "~/pages/Page"
import Router from "~/router"

import Box from "../../../../../../components/Common/Box"
import useStyles from "./styles"

const columnHelper = createColumnHelper()

const columns = [
  columnHelper.accessor((row) => row.user_id, {
    user_id: "user_id",
    header: ({ column }) => <BasicSortHeader column={column} label="User ID" />,
    id: "user_id",
  }),
  columnHelper.accessor((row) => row.username, {
    username: "username",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Username" />
    ),
    id: "username",
  }),
  columnHelper.accessor((row) => row?.first_name, {
    first_name: "first_name",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="First Name" />
    ),
    id: "first_name",

    cell: (props) => {
      const splitName = props?.row?.original?.first_name?.split(" ")
      return (
        <span className="flex flex-col">
          {splitName?.map((sso, i) => (
            <span key={i}>{sso}</span>
          ))}
        </span>
      )
    },
  }),
  columnHelper.accessor((row) => row.last_name, {
    last_name: "last_name",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Last Name" />
    ),
    id: "last_name",
  }),
  columnHelper.accessor((row) => row.email, {
    email: "email",
    header: ({ column }) => <BasicSortHeader column={column} label="Email" />,
    id: "email",
  }),
  columnHelper.accessor((row) => row.test_user, {
    test_user: "test_user",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Test User" />
    ),
    id: "test_user",
  }),

  columnHelper.accessor((row) => row.courses_completed, {
    courses_completed: "courses_completed",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Completed Courses" />
    ),
    id: "courses_completed",

    cell: (props) => {
      return <span>{props.row.original.courses_completed}</span>
    },
  }),
  columnHelper.accessor((row) => row.progress, {
    in_progress_courses: "progress",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Progress" />
    ),
    id: "progress",
  }),
  columnHelper.accessor((row) => row.has_badge, {
    has_badge: "has_badge",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Has Badge" />
    ),
    id: "has_badge",

    cell: (props) => {
      return (
        <Typography variant="subtitle1">
          {props.row.original.has_badge === 1 ? "Yes" : "No"}
        </Typography>
      )
    },
  }),
  columnHelper.accessor((row) => row.completion_date, {
    completion_date: "completion_date",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Completion Date" />
    ),
    id: "completion_data",
    cell: (props) => {
      return (
        <Typography variant="subtitle1">
          {props.row.original.completion_date === null
            ? "-"
            : dayjs(props.row.original.completion_date).format(
                "MM/DD/YYYY H:mm",
              )}
        </Typography>
      )
    },
  }),
]

const Analytics = ({ routes = [], ...rest }) => {
  usePermissionsRedirect("/learning-paths", "analytics")

  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize, setPageSize] = useState(50)
  const [searchValue, setSearchValue] = useState("")

  const classes = useStyles()
  let dataFetch = {
    searchValue: searchValue.length === 0 ? null : searchValue,
    limit: pageSize,
  }

  const { analyticsMutation, downloadAnalyticsMutation, path } =
    useContext(PathContext)

  const { data, fetchNextPage, isFetching, refetch, hasNextPage } =
    useInfiniteQuery({
      queryKey: ["users", dataFetch],
      queryFn: async ({ pageParam = 0 }) => {
        return await pathAnalytics(path?.id, {
          ...dataFetch,
          offset: pageParam,
        })
      },
      getNextPageParam: (lastPage, allPages) => {
        const nextPageData = lastPage.nextOffset
        return nextPageData
      },
    })
  useEffect(() => {
    refetch()
  }, [path?.id])

  return (
    <Page>
      <Box className={classes.container}>
        {/* <AnalyticsFilters type="path" /> */}
        <AnalyticsTable
          columns={columns}
          mutation={analyticsMutation}
          data={data}
          downloadMutation={downloadAnalyticsMutation}
          type="Learning Path"
          contentData={path}
          initialSortString={"user_id"}
          isFetching={isFetching}
          fetchNextPage={fetchNextPage}
          refetch={refetch}
          hasNextPage={hasNextPage}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          setSearchValue={setSearchValue}
          searchValue={searchValue}
        />
        <Router routes={routes} {...rest} />
      </Box>
    </Page>
  )
}

Analytics.propTypes = {
  routes: PropTypes.array,
}

export default Analytics
